import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { CheckCircle2, XCircle, Clock } from 'lucide-react';
import './SuccessPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SuccessPage = () => {
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const [status, setStatus] = useState('checking');
  const [orderInfo, setOrderInfo] = useState(null);
  const pollingRef = useRef(null);
  const cartClearedRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    
    if (!sessionId) {
      setStatus('error');
      return;
    }

    let attempts = 0;
    const maxAttempts = 8;

    const pollStatus = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/checkout/status/${sessionId}`);
        if (!response.ok) throw new Error('Status check failed');
        
        const data = await response.json();
        
        if (data.payment_status === 'paid') {
          setStatus('paid');
          setOrderInfo(data);
          if (!cartClearedRef.current) {
            clearCart();
            cartClearedRef.current = true;
          }
          return;
        }
        
        if (data.status === 'expired') {
          setStatus('expired');
          return;
        }
        
        attempts++;
        if (attempts >= maxAttempts) {
          setStatus('timeout');
          return;
        }
        
        pollingRef.current = setTimeout(pollStatus, 2000);
      } catch (err) {
        attempts++;
        if (attempts >= maxAttempts) {
          setStatus('error');
        } else {
          pollingRef.current = setTimeout(pollStatus, 2000);
        }
      }
    };

    pollStatus();

    return () => {
      if (pollingRef.current) clearTimeout(pollingRef.current);
    };
  }, [clearCart]);

  const renderContent = () => {
    if (status === 'checking') {
      return (
        <>
          <Clock size={64} className="status-icon checking" />
          <h1>Processing your payment...</h1>
          <p>Please wait, this may take a few seconds.</p>
        </>
      );
    }
    
    if (status === 'paid') {
      return (
        <>
          <CheckCircle2 size={64} className="status-icon success" />
          <h1>Thank you for your purchase!</h1>
          <p>Your payment has been received successfully.</p>
          {orderInfo && (
            <div className="order-summary">
              <p className="amount">
                ${(orderInfo.amount_total / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
              <p className="reference">Reference: {orderInfo.session_id.slice(-12)}</p>
            </div>
          )}
          <p className="note">
            We'll be in touch shortly to arrange shipping.
          </p>
        </>
      );
    }
    
    if (status === 'expired') {
      return (
        <>
          <XCircle size={64} className="status-icon error" />
          <h1>Payment session expired</h1>
          <p>Please try again from the cart.</p>
        </>
      );
    }
    
    return (
      <>
        <XCircle size={64} className="status-icon error" />
        <h1>Something went wrong</h1>
        <p>We couldn't verify your payment. Please contact us.</p>
      </>
    );
  };

  return (
    <div className="success-page">
      <div className="success-card">
        {renderContent()}
        <button className="back-button" onClick={() => navigate('/')}>
          Back to gallery
        </button>
      </div>
    </div>
  );
};

export default SuccessPage;
